import { z } from 'zod'

/**
 * How an opportunity was found, which is also why we are writing.
 *
 * In priority order, and the order is the likelihood of a draft that is worth
 * sending rather than the size of the prize. A page that already links to a
 * dead url of ours is one edit away and its owner has already chosen us once; a
 * page that mentions us without linking has already done the hard half. The
 * sweep sits above the link gap on measurement rather than on argument: on
 * esimguide's first run the gap produced one draft from fifty-five reads — the
 * pages linking competitors are big and foreign publishers behind contact
 * forms — where the sweep surfaces one writer's country guide at a time.
 */
export const OUTREACH_SOURCES = ['reclaim', 'mention', 'sweep', 'gap'] as const
export type OutreachSource = (typeof OUTREACH_SOURCES)[number]

export const outreachSourceSchema = z.enum(OUTREACH_SOURCES)

export const OUTREACH_SOURCE_LABELS: Record<OutreachSource, string> = {
  reclaim: 'Dead link to us',
  mention: 'Mentions us',
  gap: 'Links a competitor',
  sweep: 'Writes about it',
}

/** Lower first. Stored on the row so the research queue is one index scan. */
export function outreachSourcePriority(source: OutreachSource): number {
  return OUTREACH_SOURCES.indexOf(source)
}

/**
 * What discovery saw, frozen on the row. One shape per source so the judge's
 * prompt and the interface can both say *why* this page, in the page's terms.
 */
export type OutreachSourceDetail =
  | { source: 'reclaim'; deadUrl: string; anchor: string | null; status: number | null }
  | { source: 'mention'; query: string; position: number }
  | {
      source: 'gap'
      competitor: string
      linksTo: string | null
      anchor: string | null
      competitors: number
    }
  | { source: 'sweep'; query: string; position: number }

/** One sentence the judge and the interface both read. */
export function describeOutreachSource(detail: OutreachSourceDetail): string {
  switch (detail.source) {
    case 'reclaim':
      return `This page links to ${detail.deadUrl}${detail.anchor ? ` (anchored "${detail.anchor}")` : ''}, a page of ours that no longer answers${detail.status ? ` (${detail.status})` : ''}.`
    case 'mention':
      return `This page came up at position ${detail.position} when searching for ${detail.query}: it may mention us without linking.`
    case 'gap':
      return `This page links to ${detail.linksTo ?? detail.competitor}${detail.anchor ? ` (anchored "${detail.anchor}")` : ''}, a competitor of ours.${detail.competitors > 1 ? ` The site links to ${detail.competitors} of our competitors.` : ''}`
    case 'sweep':
      return `This page ranks at position ${detail.position} for "${detail.query}".`
  }
}
